// src/hooks/useAssessment/useConsultantGuidance.js
import { useState, useCallback } from 'react';
import toast from 'react-hot-toast';
import logger from '../../utils/logger';

/**
 * Manages consultant guidance flow for the assessment:
 * - requestingConsultant, showGuidanceModal, userHasChosen
 * - handleRequestConsultantGuidance (API call + navigation)
 * - preserves all logging and toast/error behavior
 */
export const useConsultantGuidance = ({ type, navigate }) => {
  const [showGuidanceModal, setShowGuidanceModal] = useState(true); // Show modal on initial load
  const [userHasChosen, setUserHasChosen] = useState(false); // Track if user made a choice
  const [requestingConsultant, setRequestingConsultant] = useState(false);

  const handleRequestConsultantGuidance = useCallback(async () => {
    setRequestingConsultant(true);

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
      const assessmentType = type === 'ESA' ? 'ESA' : 'EMHA';

      const response = await fetch(`${backendUrl}/api/live-session/request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          assessment_type: assessmentType
        })
      });

      const data = await response.json();

      // Check for errors in response (even if status is 200)
      if (!response.ok || !data.success) {
        // Extract message from different possible error formats
        const errorMessage =
          data.message || data.detail || data.error || 'Failed to request consultant session';
        throw new Error(errorMessage);
      }

      if (data.success) {
        setUserHasChosen(true);
        // Navigate to dashboard where user can see pending session
        navigate('/dashboard', {
          state: {
            message: 'Session request sent! Your consultant will be notified.',
            showPending: true
          }
        });
      }
    } catch (err) {
      logger.error('Error requesting consultant guidance:', err);
      const errorMessage =
        err?.message || 'Failed to request consultant session. Please try again.';

      // Handle no consultant assigned error specially
      if (
        errorMessage.includes("don't have an assigned consultant") ||
        errorMessage.includes('consultant assigned')
      ) {
        toast.error(
          'No Consultant Assigned: A consultant has not been assigned to your account yet. Please contact your administrator or proceed with a self-guided assessment.',
          { duration: 6000 }
        );
      } else {
        toast.error(errorMessage, { duration: 5000 });
      }

      setShowGuidanceModal(false);
    } finally {
      setRequestingConsultant(false);
    }
  }, [navigate, type]);

  return {
    requestingConsultant,
    showGuidanceModal,
    userHasChosen,
    handleRequestConsultantGuidance,
    // Expose setters so existing JSX (onClick, etc.) can control modal and choice
    setShowGuidanceModal,
    setUserHasChosen
  };
};


